import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  addMenuOptionGroup,
  updateMenuOptionGroup,
  deleteMenuOptionGroup,
  fetchMenuOptionGroups,
} from '../../../Redux/MenuOption/menuOptionSlice';

interface MenuOptionGroup {
  id: string;
  name: string;
  sortIndex: number;
  /** 必須グループかどうか。BE 未対応レスポンスでは欠落するため、欠落時は true（必須）として扱う */
  required?: boolean;
}

interface MenuOptionGroupManagementProps {
  storeId: string;
}

const MenuOptionGroupManagement: React.FC<MenuOptionGroupManagementProps> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [newGroupName, setNewGroupName] = useState('');
  // 新規グループの必須/任意（デフォルトは従来どおり必須）
  const [newGroupRequired, setNewGroupRequired] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const menuOptionGroups = useSelector((state: RootState) => state.menuOption.menuOptionGroups) || [];
  const loading = useSelector((state: RootState) => state.menuOption.loading);

  useEffect(() => {
    if (storeId) {
      dispatch(fetchMenuOptionGroups({ storeId, menuId: 'default' })).catch((error: Error) =>
        setError(error.message)
      );
    }
  }, [dispatch, storeId]);

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) {
      setError('グループ名を入力してください');
      return;
    }

    try {
      await dispatch(
        addMenuOptionGroup({
          storeId,
          name: newGroupName,
          sortIndex: menuOptionGroups.length,
          required: newGroupRequired,
        })
      ).unwrap();
      // 追加後に最新のグループ一覧を取得
      await dispatch(fetchMenuOptionGroups({ storeId, menuId: 'default' }));
      setNewGroupName('');
      setNewGroupRequired(true);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  // 既存グループの必須/任意を切り替える
  const handleToggleRequired = async (group: MenuOptionGroup, required: boolean) => {
    try {
      await dispatch(
        updateMenuOptionGroup({
          storeId,
          id: group.id,
          required,
        })
      ).unwrap();
      // 更新後に最新のグループ一覧を取得
      await dispatch(fetchMenuOptionGroups({ storeId, menuId: 'default' }));
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await dispatch(deleteMenuOptionGroup({ storeId, id: groupId })).unwrap();
      // 削除後に最新のグループ一覧を取得
      await dispatch(fetchMenuOptionGroups({ storeId, menuId: 'default' }));
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          オプショングループの追加
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="グループ名"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            error={!!error}
            helperText={error}
            fullWidth
          />
          <Button variant="contained" onClick={handleAddGroup} disabled={loading}>
            追加
          </Button>
        </Box>
        <Box sx={{ mt: 2 }}>
          <ToggleButtonGroup
            value={newGroupRequired ? 'required' : 'optional'}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (value !== null) {
                setNewGroupRequired(value === 'required');
              }
            }}
          >
            <ToggleButton value="required">必須</ToggleButton>
            <ToggleButton value="optional">任意</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {newGroupRequired
              ? '必須: オプションを選択しないと注文できません'
              : '任意: オプションを選択しなくても注文できます'}
          </Typography>
        </Box>
      </Box>

      <Typography variant="h6" gutterBottom>
        オプショングループ一覧
      </Typography>
      <List>
        {Array.isArray(menuOptionGroups) && menuOptionGroups.map((group: MenuOptionGroup) => (
          group && group.id && (
            <ListItem key={group.id}>
              <ListItemText
                primary={group.name || '名称未設定'}
                secondary={
                  // required 欠落（BE 未対応）時は従来どおり必須扱い
                  group.required !== false
                    ? '必須（選択しないと注文できない）'
                    : '任意（選択しなくても注文できる）'
                }
              />
              <ListItemSecondaryAction>
                <FormControlLabel
                  control={
                    <Switch
                      checked={group.required !== false}
                      onChange={(e) => handleToggleRequired(group, e.target.checked)}
                      disabled={loading}
                      size="small"
                    />
                  }
                  label="必須"
                  sx={{ mr: 1 }}
                />
                <IconButton edge="end" onClick={() => handleDeleteGroup(group.id)} disabled={loading}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );
};

export default MenuOptionGroupManagement;
